import { Component, Input, OnInit } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Store, select } from '@ngrx/store';
import { map, Observable } from 'rxjs';

import { Breakpoint } from '@fcom/common/interfaces/breakpoint.interface';
import { Duration } from '@fcom/dapi';
import { resolveTailName } from '@fcom/core/utils';

import { Cabin, FlightsV2, FlightV2, TransportType } from '../../api/models';
import { getFlightById } from '../../utils/basket.utils';
import { getBasketFlights, getStage } from '../../store/selectors';
import { State, Stage, ExtendedFlightBasket } from '../../interfaces';
import { UpgradeStatus } from '../../api/models/upgrade-status';

@Component({
  selector: 'fin-upgrade-bound-card',
  templateUrl: 'bound-card.component.html',
  styleUrls: ['bound-card.component.scss'],
})
export class BoundCardComponent implements OnInit {
  readonly UpgradeStatus = UpgradeStatus;
  readonly Breakpoint = Breakpoint;
  readonly Stage = Stage;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TransportType = TransportType;

  @Input()
  flight: FlightV2;

  @Input()
  allFlights: FlightsV2;

  @Input()
  numberOfFlights: number;

  @Input()
  first: boolean;

  @Input()
  last: boolean;

  @Input()
  highlighted: boolean;

  @Input()
  bordered: boolean;

  currentStage$: Observable<Stage>;
  flightInBasket$: Observable<ExtendedFlightBasket>;

  mapTransportTypeToIcon: Record<TransportType, { arrival: SvgLibraryIcon; departure: SvgLibraryIcon }> = {
    [TransportType.Bus]: {
      arrival: SvgLibraryIcon.BUS,
      departure: SvgLibraryIcon.BUS,
    },
    [TransportType.Train]: {
      arrival: SvgLibraryIcon.TRAIN,
      departure: SvgLibraryIcon.TRAIN,
    },
    [TransportType.Flight]: {
      arrival: SvgLibraryIcon.ARRIVAL,
      departure: SvgLibraryIcon.DEPARTURE,
    },
    [TransportType.Unknown]: {
      arrival: SvgLibraryIcon.HELP,
      departure: SvgLibraryIcon.HELP,
    },
  };

  constructor(private store: Store<State>) {}

  ngOnInit(): void {
    this.currentStage$ = this.store.pipe(select(getStage));
    this.flightInBasket$ = this.store.pipe(select(getBasketFlights)).pipe(
      map((flightBasket) => {
        return getFlightById(flightBasket, this.flight.segmentInfo.flightId);
      })
    );
  }

  resolveTails(airlineCode: string): string[] {
    return airlineCode ? [resolveTailName(airlineCode)] : [];
  }

  formatDuration(minutes: number): Duration {
    return {
      hours: Math.floor(minutes / 60),
      minutes: Math.ceil(minutes % 60),
      milliseconds: 0,
    };
  }

  getFlightTypeI18n(cabin: Cabin, transportType: TransportType): string {
    const map = {
      [Cabin.Economy]: `oneUpgrade.tabs.${Cabin.Economy.toLowerCase()}`,
      [Cabin.Ecopremium]: `oneUpgrade.tabs.${Cabin.Ecopremium.toLowerCase()}`,
      [Cabin.Business]: `oneUpgrade.tabs.${Cabin.Business.toLowerCase()}`,
      [TransportType.Bus]: 'oneUpgrade.transportType.bus',
      [TransportType.Train]: 'oneUpgrade.transportType.train',
      [TransportType.Unknown]: 'oneUpgrade.transportType.unknown',
    };

    return map[transportType] || map[cabin];
  }

  showAsStopOver(currentFlight: FlightV2, type: 'arrival' | 'departure'): boolean {
    const { ordinal, transportType, group } = currentFlight.segmentInfo;
    const searchOrdinal = type === 'departure' ? ordinal - 1 : ordinal + 1;
    const foundFlight = this.allFlights.find((flight) => {
      return flight.segmentInfo.group === group && flight.segmentInfo.ordinal === searchOrdinal;
    });

    if (!foundFlight || transportType !== TransportType.Flight) {
      return false;
    }

    return foundFlight.segmentInfo.transportType === TransportType.Flight;
  }
}
